import { ArrowUpRight, Sparkles } from "lucide-react";

const TERMINAL_LINES = [
  { prompt: "$", text: "contentium init --stack content,ai,learning", delay: 100 },
  { prompt: ">", text: "ingesting raw curriculum...", delay: 1100 },
  { prompt: ">", text: "structuring with AI pipelines...", delay: 2100 },
  { prompt: ">", text: "delivering to learners ✓", delay: 3100 },
];

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden border-b border-slate-200/70 bg-[#FAFAFA]"
    >
      {/* drifting dotted grid */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-60 ct-grid-drift"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(15, 23, 42, 0.08) 1px, transparent 0)",
          backgroundSize: "28px 28px",
        }}
      />
      {/* pulsing soft orb */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 right-[-10%] h-[520px] w-[520px] rounded-full bg-neutral-300/45 blur-3xl ct-orb-pulse"
      />

      <div className="ct-container relative z-10 grid gap-14 py-20 md:py-28 lg:grid-cols-12 lg:py-32">
        <div className="lg:col-span-7">
          <div className="reveal flex items-center gap-2">
            <span className="ct-eyebrow" data-testid="hero-eyebrow">
              <Sparkles className="h-3.5 w-3.5" /> AI. Content. Learning.
            </span>
          </div>

          <h1
            className="reveal reveal-delay-1 mt-6 text-balance font-display text-4xl font-bold leading-tight tracking-tight text-[#0F172A] sm:text-5xl"
            data-testid="hero-title"
          >
            Engineering content with AI,
            <br />
            <span className="bg-gradient-to-br from-[#0A0A0A] via-[#404040] to-[#737373] bg-clip-text text-transparent">
              at the speed of curiosity.
            </span>
          </h1>

          <p
            className="reveal reveal-delay-2 mt-7 max-w-2xl text-lg leading-relaxed text-slate-600"
            data-testid="hero-subtitle"
          >
            Contentium Tech is a technology-driven content company combining content,
            automation, and AI to build platforms that make learning simple, accessible,
            and impactful at scale.
          </p>

          <div className="reveal reveal-delay-3 mt-10 flex flex-wrap items-center gap-4">
            <a
              href="/contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-[#0F172A] px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#1e293b]"
            >
              Get in touch
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="/eduzy"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3.5 text-sm font-semibold text-[#0F172A] transition-all duration-300 hover:border-[#0A0A0A]"
            >
              Meet Eduzy
            </a>
          </div>
        </div>

        {/* Terminal */}
        <div className="reveal reveal-delay-4 self-end lg:col-span-5">
          <div className="relative">
            <div className="absolute -inset-3 rounded-3xl bg-gradient-to-tr from-neutral-200 via-transparent to-transparent blur-xl" />
            <div
              data-testid="hero-terminal"
              className="relative overflow-hidden rounded-2xl border border-neutral-200 bg-[#0A0A0A] font-mono text-[13px] leading-relaxed text-neutral-100 shadow-2xl"
              style={{ fontFamily: "'Source Code Pro', ui-monospace, monospace" }}
            >
              <div className="flex items-center gap-2 border-b border-neutral-800 bg-[#171717] px-4 py-3">
                <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
                <span className="ml-3 text-[11px] uppercase tracking-[0.2em] text-neutral-500">
                  contentium ~ pipeline
                </span>
              </div>
              <div className="px-5 py-5">
                {TERMINAL_LINES.map((line, i) => (
                  <div
                    key={i}
                    className="ct-term-line flex gap-2"
                    style={{ animationDelay: `${line.delay}ms` }}
                  >
                    <span
                      className={
                        line.prompt === "$" ? "text-[#737373]" : "text-neutral-500"
                      }
                    >
                      {line.prompt}
                    </span>
                    <span className="text-neutral-100">{line.text}</span>
                  </div>
                ))}
                <div
                  className="ct-term-line flex items-center gap-2"
                  style={{ animationDelay: `4100ms` }}
                >
                  <span className="text-[#737373]">$</span>
                  <span className="ct-cursor inline-block h-4 w-2 bg-neutral-100" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* marquee strip */}
      <div className="relative z-10 border-t border-slate-200/70 bg-white/60">
        <div className="overflow-hidden">
          <div className="ct-marquee-track flex w-max items-center gap-12 whitespace-nowrap py-4 font-display text-sm uppercase tracking-[0.22em] text-slate-500">
            {[
              "Curriculum Engineering",
              "Retrieval Augmented",
              "Adaptive Pacing",
              "Parental Trust",
              "Built in India",
              "Global Mindset",
            ]
              .concat([
                "Curriculum Engineering",
                "Retrieval Augmented",
                "Adaptive Pacing",
                "Parental Trust",
                "Built in India",
                "Global Mindset",
              ])
              .map((t, i) => (
                <span key={i} className="flex items-center gap-12">
                  {t}
                  <span className="h-1 w-1 rounded-full bg-[#0A0A0A]" />
                </span>
              ))}
          </div>
        </div>
      </div>
    </section>
  );
}
